import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as svgPanZoom from 'svg-pan-zoom';

import { AnalyticsMetric, StatsService } from 'src/app/services/stats.service';

import { HomeWidget } from '../dashboard-widget.utils';

export interface VertexTarget {
  feature: string;
  weight: number;
  path: string; // line render
}

export interface Vertex {
  feature: string;
  name: string;
  x?: number;
  y?: number;
  xpos?: number;
  ypos?: number;
  targets: VertexTarget[];
  dataset: Dataset;
}

export interface Dataset {
  feature: string;
  totalIn: number;
  totalOut: number;
  in: { [feature in string]: number };
  out: { [feature in string]: number };
  relIn: { [feature in string]: number };
  relOut: { [feature in string]: number };
}

@Component({
  selector: 'app-transition-graph-widget',
  templateUrl: './transition-graph-widget.component.html',
  styleUrls: ['./transition-graph-widget.component.scss']
})
export class TransitionGraphWidgetComponent extends HomeWidget implements OnInit, OnDestroy {
  @ViewChild('transitionGraph') transitionGraph;

  public vertices: Vertex[];
  public hover: Vertex = null;

  public loading = false;

  private panZoom: SvgPanZoom.Instance = null;

  constructor(elRef: ElementRef, private stats: StatsService) {
    super(elRef);
  }

  public ngOnInit(): void {
    this.loading = true;
    this.stats
      .fetchMetrics(
        AnalyticsMetric.transition,
        false,
        [],
        this.settings.filter,
        this.settings.exclude,
        this.settings.from,
        this.settings.to
      )
      .then(data => {
        this.loading = false;
        return this.initGraph(data);
      })
      .then(() => {
        setTimeout(() => {
          if (this.transitionGraph) {
            this.panZoom = svgPanZoom(this.transitionGraph.nativeElement, { controlIconsEnabled: true, mouseWheelZoomEnabled: false });
            this.panZoom.center();
          }
        }, 5);
      });
  }

  public ngOnDestroy() {
    if (this.panZoom) {
      this.panZoom.destroy();
    }
  }

  private path(s: Vertex, t: Vertex): string {
    let sx = s.xpos; // source
    let sy = s.ypos;
    let tx = t.xpos; // target
    const ty = t.ypos;
    let cx = tx; // courve
    let cy = ty;

    if (sy === ty) {
      cx = (sx + tx) / 2;
      cy = ty + (tx - sx) / 2;

      if (sx > tx) {
        const temp = sx;
        sx = tx;
        tx = temp;
        sy -= 50;
      } else {
        sy += 50;
      }
    }

    if (sx > tx && sy < ty) {
      // Flip for text alignment
      return `M ${tx} ${ty} Q ${cx} ${cy} ${sx} ${sy}`;
    }

    return `M ${sx} ${sy} Q ${cx} ${cy} ${tx} ${ty}`;
  }

  private initGraph(data: Dataset[]) {
    // Setup vertices
    const verts: Vertex[] = data.map(src => {
      return {
        feature: src.feature,
        name: src.feature,
        y: src.feature === 'login' ? 0 : src.feature === 'logout' ? 2 : 1,
        targets: [],
        dataset: src
      };
    });

    // Add implicit logout vertices
    if (verts.length > 0 && !verts.some(v => v.feature === 'logout')) {
      verts.push({ feature: 'logout', name: 'logout', y: 2, targets: [], dataset: null });
    }

    // Capitalize vertex names
    verts.forEach(vert => (vert.name = vert.name.charAt(0).toUpperCase() + vert.name.substring(1)));

    // Calculate vertex positions
    const hierachy: Vertex[][] = [];
    for (const vert of verts) {
      hierachy[vert.y] = [...(hierachy[vert.y] || []), vert];
    }
    const width = 2048;
    for (let y = 0; y < hierachy.length; y++) {
      const existing = hierachy[y];
      const length = existing?.length ?? 0;
      const space = width / (length + 1);
      for (let i = 0; i < length; i++) {
        const v = hierachy[y][i];
        v.xpos = (i + 1) * space;
        v.ypos = 150 + y * 500;
      }
    }

    // Setup targets and transition paths for rendering

    for (const vert of verts) {
      if (vert.dataset) {
        for (const out in vert.dataset.relOut) {
          const dst = verts.find(v => v.feature === out);
          vert.targets.push({
            feature: out,
            weight: vert.dataset.relOut[out],
            path: this.path(vert, dst)
          });
        }
      }
    }

    this.vertices = verts;
  }
}
